/**
 * Persistent queue for failed folder photo uploads (IndexedDB).
 * React-free: enqueue on failure; flush on online / visibility (registered from App).
 */
import type { SubfolderType } from "@shared/api";
import {
  appendOfflineSyncFailure,
  readApiErrorBody,
} from "@/lib/offline-sync-failure-log";

const DB_NAME = "tpt-crm-image-upload-outbox";
const DB_VERSION = 1;
const STORE = "items";
const FLUSH_MAX_PASSES = 5;
const ENQUEUE_FLUSH_DEBOUNCE_MS = 500;

export type ImageUploadOutboxContext = {
  folderId: string;
  dealId?: string;
};

export type ImageUploadOutboxEnqueuePayload = {
  url: string;
  files: File[];
  gpsHints: string;
  capturedAtHints?: string;
  /** Extra multipart string fields replayed verbatim (e.g. subfolderType, dealId, productSlug). */
  fields?: Record<string, string>;
  /** Multipart field name used for the file(s). Default `"files"`. */
  fileFieldName?: string;
  context: ImageUploadOutboxContext;
  /** @deprecated use `fields.subfolderType`; kept for older call sites. */
  subfolderType?: SubfolderType;
  /** @deprecated use `fields.productSlug`. */
  productSlug?: string;
};

export type ImageUploadOutboxListItem = {
  id: string;
  createdAt: number;
  url: string;
  subfolderType: SubfolderType;
  fileNames: string[];
  productSlug?: string;
  context: ImageUploadOutboxContext;
};

type StoredRecord = {
  id: string;
  createdAt: number;
  url: string;
  files: { blob: Blob; filename: string; mimeType: string }[];
  gpsHints: string;
  capturedAtHints?: string;
  /** Generic extra fields (target-agnostic). */
  fields?: Record<string, string>;
  fileFieldName?: string;
  context: ImageUploadOutboxContext;
  /** Legacy top-level fields (records queued before generalization). */
  subfolderType?: SubfolderType;
  productSlug?: string;
  /** Failed online flush attempts (transient errors). Dead-lettered past the cap. */
  attempts?: number;
};

/** After this many transient (online) failures, drop so a broken upload can't clog the queue forever. */
const MAX_TRANSIENT_ATTEMPTS = 20;

/** Resolve the effective extra multipart fields, merging legacy top-level keys. */
function resolveRecordFields(record: StoredRecord): Record<string, string> {
  const fields: Record<string, string> = { ...(record.fields ?? {}) };
  if (record.subfolderType && fields.subfolderType == null) fields.subfolderType = record.subfolderType;
  if (record.productSlug && fields.productSlug == null) fields.productSlug = record.productSlug;
  return fields;
}

type SuccessDetail = {
  id: string;
  context: ImageUploadOutboxContext;
  response: unknown;
  files: File[];
};

type FailureDetail = {
  id: string;
  context: ImageUploadOutboxContext;
  status: number;
  fileNames: string[];
  errorCode?: string;
  message?: string;
};

async function emitImageFailure(
  record: StoredRecord,
  status: number,
  apiError?: { errorCode?: string; message?: string },
): Promise<void> {
  const fileNames = record.files.map((f) => f.filename);
  await appendOfflineSyncFailure({
    kind: "image",
    status,
    title: fileNames.join(", ") || "upload",
    errorCode: apiError?.errorCode ?? (status === 0 ? "network_or_timeout" : undefined),
    message: apiError?.message,
    dealId: record.context.dealId,
  });
  failureBridge?.({
    id: record.id,
    context: record.context,
    status,
    fileNames,
    errorCode: apiError?.errorCode,
    message: apiError?.message,
  });
}

type ProcessResult = { ok: boolean; haltQueue: boolean };

/** Treat non-retryable 4xx (e.g. 413 too large, 415 bad type, 400) as dead-letter; keep 5xx/408/429. */
function classifyUploadStatus(status: number): "halt" | "drop" | "retry" {
  if (status === 401 || status === 403) return "halt";
  if (status === 408 || status === 429) return "retry";
  if (status >= 400 && status < 500) return "drop";
  return "retry";
}

/** Thrown after a failed upload was stored in the outbox (ImagesFolder mutation onError handles it). */
export class FolderPhotoUploadQueuedLocallyError extends Error {
  constructor() {
    super("folder_photo_upload_queued_locally");
    this.name = "FolderPhotoUploadQueuedLocallyError";
  }
}

let successBridge: ((detail: SuccessDetail) => void) | null = null;
let failureBridge: ((detail: FailureDetail) => void) | null = null;
let flushPromise: Promise<void> | null = null;
let enqueueFlushTimer: ReturnType<typeof setTimeout> | null = null;
const pendingListeners = new Set<(n: number) => void>();

function notifyPendingListeners(): void {
  void getPendingCount().then((n) => {
    pendingListeners.forEach((fn) => {
      try {
        fn(n);
      } catch {
        /* ignore */
      }
    });
  });
}

function scheduleFlushAfterEnqueue(): void {
  if (typeof window === "undefined") return;
  if (enqueueFlushTimer) clearTimeout(enqueueFlushTimer);
  enqueueFlushTimer = setTimeout(() => {
    enqueueFlushTimer = null;
    void flushImageUploadOutbox();
  }, ENQUEUE_FLUSH_DEBOUNCE_MS);
}

export function setImageUploadOutboxSuccessBridge(handler: ((detail: SuccessDetail) => void) | null): void {
  successBridge = handler;
}

export function setImageUploadOutboxFailureBridge(handler: ((detail: FailureDetail) => void) | null): void {
  failureBridge = handler;
}

export function subscribeImageUploadOutboxPendingCount(listener: (count: number) => void): () => void {
  pendingListeners.add(listener);
  void getPendingCount().then(listener);
  return () => {
    pendingListeners.delete(listener);
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
  });
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error ?? new Error("indexedDB request failed"));
  });
}

async function loadAllRecordsSorted(): Promise<StoredRecord[]> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const allReq = tx.objectStore(STORE).getAll();
  const rows = (await idbRequest(allReq)) as StoredRecord[];
  db.close();
  return rows.sort((a, b) => a.createdAt - b.createdAt);
}

export async function enqueueFolderPhotosUpload(payload: ImageUploadOutboxEnqueuePayload): Promise<string> {
  const id = crypto.randomUUID();
  const createdAt = Date.now();
  const files: StoredRecord["files"] = payload.files.map((f) => ({
    blob: f,
    filename: f.name,
    mimeType: f.type || "application/octet-stream",
  }));

  const fields: Record<string, string> = { ...(payload.fields ?? {}) };
  if (payload.subfolderType && fields.subfolderType == null) fields.subfolderType = payload.subfolderType;
  if (payload.productSlug && fields.productSlug == null) fields.productSlug = payload.productSlug;

  const record: StoredRecord = {
    id,
    createdAt,
    url: payload.url,
    files,
    gpsHints: payload.gpsHints,
    capturedAtHints: payload.capturedAtHints,
    fields,
    fileFieldName: payload.fileFieldName,
    context: payload.context,
  };

  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).add(record));
  db.close();
  notifyPendingListeners();
  scheduleFlushAfterEnqueue();
  return id;
}

export async function removeImageUploadOutboxItem(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  await idbRequest(tx.objectStore(STORE).delete(id));
  db.close();
  notifyPendingListeners();
}

export async function getPendingCount(): Promise<number> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readonly");
  const countReq = tx.objectStore(STORE).count();
  const n = await idbRequest(countReq);
  db.close();
  return n;
}

export async function listImageUploadOutboxItems(): Promise<ImageUploadOutboxListItem[]> {
  const rows = await loadAllRecordsSorted();
  return rows.map((r) => {
    const fields = resolveRecordFields(r);
    return {
      id: r.id,
      createdAt: r.createdAt,
      url: r.url,
      subfolderType: (fields.subfolderType as SubfolderType) ?? "munkaink",
      fileNames: r.files.map((f) => f.filename),
      productSlug: fields.productSlug,
      context: r.context,
    };
  });
}

function recordsToFiles(record: StoredRecord): File[] {
  return record.files.map(
    (f) => new File([f.blob], f.filename, { type: f.mimeType || f.blob.type || "image/jpeg" }),
  );
}

function buildFormData(record: StoredRecord): FormData {
  const fd = new FormData();
  const files = recordsToFiles(record);
  const fileField = record.fileFieldName || "files";
  for (const f of files) {
    fd.append(fileField, f);
  }
  fd.append("gpsHints", record.gpsHints);
  if (record.capturedAtHints) {
    fd.append("capturedAtHints", record.capturedAtHints);
  }
  const fields = resolveRecordFields(record);
  for (const [k, v] of Object.entries(fields)) {
    if (typeof v === "string" && v !== "") fd.append(k, v);
  }
  return fd;
}

async function processOne(record: StoredRecord): Promise<ProcessResult> {
  const fd = buildFormData(record);
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 120_000);
  try {
    const r = await fetch(record.url, {
      method: "POST",
      credentials: "include",
      body: fd,
      signal: controller.signal,
    });
    if (!r.ok) {
      const outcome = classifyUploadStatus(r.status);
      if (outcome === "drop") {
        const apiError = await readApiErrorBody(r);
        await removeImageUploadOutboxItem(record.id);
        await emitImageFailure(record, r.status, apiError);
        return { ok: false, haltQueue: false };
      }
      if (outcome === "halt") return { ok: false, haltQueue: true };
      // Transient (5xx/408/429): count the failure and dead-letter once hopeless.
      return handleTransientFailure(record, r.status);
    }
    let response: unknown;
    try {
      response = await r.json();
    } catch {
      response = null;
    }
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    await idbRequest(tx.objectStore(STORE).delete(record.id));
    db.close();
    notifyPendingListeners();
    const files = recordsToFiles(record);
    successBridge?.({ id: record.id, context: record.context, response, files });
    return { ok: true, haltQueue: false };
  } catch {
    return handleTransientFailure(record, 0);
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Bump the transient-failure counter for a queued upload. Returns the new attempt count.
 * Only called for online failures, so a long offline period never burns attempts.
 */
async function bumpAttempts(id: string): Promise<number> {
  const db = await openDb();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const current = (await idbRequest(store.get(id))) as StoredRecord | undefined;
  if (!current) {
    db.close();
    return Number.MAX_SAFE_INTEGER;
  }
  const attempts = (current.attempts ?? 0) + 1;
  await idbRequest(store.put({ ...current, attempts }));
  db.close();
  return attempts;
}

/**
 * Handle a transient upload failure (5xx / 408 / 429 / network / timeout): bump the attempt
 * counter and dead-letter the upload once it has failed too many times, so a permanently
 * broken upload cannot clog the queue forever.
 */
async function handleTransientFailure(record: StoredRecord, status: number): Promise<ProcessResult> {
  const attempts = await bumpAttempts(record.id);
  if (attempts >= MAX_TRANSIENT_ATTEMPTS) {
    await removeImageUploadOutboxItem(record.id);
    await emitImageFailure(record, status, { errorCode: "max_retries_exceeded" });
    return { ok: false, haltQueue: false };
  }
  return { ok: false, haltQueue: false };
}

async function runFlushPass(): Promise<{ anySuccess: boolean; haltQueue: boolean }> {
  const rows = await loadAllRecordsSorted();
  let anySuccess = false;
  let haltQueue = false;

  for (const record of rows) {
    const result = await processOne(record);
    if (result.ok) {
      anySuccess = true;
    } else if (result.haltQueue) {
      haltQueue = true;
      break;
    }
  }

  return { anySuccess, haltQueue };
}

async function runFlush(): Promise<void> {
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return;
  }

  for (let pass = 0; pass < FLUSH_MAX_PASSES; pass++) {
    const pendingBefore = await getPendingCount();
    if (pendingBefore === 0) {
      return;
    }

    const { anySuccess, haltQueue } = await runFlushPass();
    if (haltQueue) {
      return;
    }

    const pendingAfter = await getPendingCount();
    if (pendingAfter === 0) {
      return;
    }
    if (!anySuccess) {
      return;
    }
  }
}

/** Single-flight flush: concurrent callers share one run. Skips failed items; halts on 401/403. */
export function flushImageUploadOutbox(): Promise<void> {
  if (!flushPromise) {
    flushPromise = runFlush().finally(() => {
      flushPromise = null;
    });
  }
  return flushPromise;
}
